import React, { useState, useEffect, useContext } from "react";
import { 
  CreditCard, User, Landmark, ShieldCheck, RefreshCw, PlusCircle, 
  Settings, CheckCircle, Clock, Trash2, Calendar, IndianRupee, X 
} from "lucide-react";
import { employeeWorkforceAPI, staffAPI } from "../../api/apiService";
import { NotificationContext } from "../../context/NotificationContext";
import { motion } from "framer-motion";
import EmptyState from "../../components/EmptyState";

export default function PayrollManagement() {
  const { addNotification } = useContext(NotificationContext);
  const [employees, setEmployees] = useState([]);
  const [salaryConfigs, setSalaryConfigs] = useState([]);
  const [payrolls, setPayrolls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [confirmPaidId, setConfirmPaidId] = useState(null);

  // Configuration Form State
  const [configEmployeeId, setConfigEmployeeId] = useState("");
  const [basicSalary, setBasicSalary] = useState("");
  const [bonus, setBonus] = useState("0");
  const [travelAllowance, setTravelAllowance] = useState("0");
  const [pf, setPf] = useState("0");
  const [tax, setTax] = useState("0");
  const [lossOfPayPerDay, setLossOfPayPerDay] = useState("500");

  // Generation Form State
  const [genEmployeeId, setGenEmployeeId] = useState("");
  const [genMonth, setGenMonth] = useState(""); // YYYY-MM
  const [filterMonth, setFilterMonth] = useState(""); // filter list by month
  const [genBasicSalary, setGenBasicSalary] = useState("");
  const [genBonus, setGenBonus] = useState("");
  const [genTravelAllowance, setGenTravelAllowance] = useState("");
  const [genPf, setGenPf] = useState("");
  const [genTax, setGenTax] = useState("");
  const [genLossOfPayPerDay, setGenLossOfPayPerDay] = useState("");

  const fetchData = async () => {
    setLoading(true);
    try {
      const [staffRes, configRes, payRes] = await Promise.all([
        staffAPI.getAllStaff(),
        employeeWorkforceAPI.getSalaryConfigs(),
        employeeWorkforceAPI.getPayrolls()
      ]);
      setEmployees(staffRes.data);
      setSalaryConfigs(configRes.data);
      setPayrolls(payRes.data);
    } catch (err) {
      console.error(err);
      addNotification("Failed to load payroll details", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // Default current month for generator YYYY-MM
    const today = new Date();
    const mm = String(today.getMonth() + 1).padStart(2, '0');
    const yyyy = today.getFullYear();
    setGenMonth(`${yyyy}-${mm}`);
    setFilterMonth(`${yyyy}-${mm}`);
  }, []);

  // Update Config fields when employee selection changes
  const handleConfigEmployeeChange = (empId) => {
    setConfigEmployeeId(empId);
    if (!empId) {
      setBasicSalary("");
      setBonus("0");
      setTravelAllowance("0");
      setPf("0");
      setTax("0");
      setLossOfPayPerDay("500");
      return;
    }
    
    // Check if configuration already exists for this employee
    const match = salaryConfigs.find(c => String(c.employee) === String(empId));
    if (match) {
      setBasicSalary(match.basic_salary);
      setBonus(match.bonus);
      setTravelAllowance(match.travel_allowance);
      setPf(match.pf);
      setTax(match.tax);
      setLossOfPayPerDay(match.loss_of_pay_per_day);
    } else {
      // Clear or set default
      setBasicSalary("");
      setBonus("0");
      setTravelAllowance("0");
      setPf("0");
      setTax("0");
      setLossOfPayPerDay("500");
    }
  };

  // Save Salary configuration
  const handleSaveConfig = async (e) => {
    e.preventDefault();
    if (!configEmployeeId) {
      addNotification("Please select an employee.", "warning");
      return;
    }
    if (!basicSalary || parseFloat(basicSalary) <= 0) {
      addNotification("Basic salary must be greater than zero.", "warning");
      return;
    }

    setActionLoading(true);
    try {
      const payload = {
        employee: configEmployeeId,
        basic_salary: basicSalary,
        bonus: bonus || "0",
        travel_allowance: travelAllowance || "0",
        pf: pf || "0",
        tax: tax || "0",
        loss_of_pay_per_day: lossOfPayPerDay || "0"
      };
      await employeeWorkforceAPI.saveSalaryConfig(payload);
      addNotification("Salary configuration saved successfully!", "success");
      
      // Reload configurations
      const configRes = await employeeWorkforceAPI.getSalaryConfigs();
      setSalaryConfigs(configRes.data);
    } catch (err) {
      console.error(err);
      addNotification("Failed to save salary structure.", "error");
    } finally {
      setActionLoading(false);
    }
  };

  // Generate Payroll (fetches calculated preview first)
  const handleGeneratePayroll = async (e) => {
    e.preventDefault();
    if (!genEmployeeId) {
      addNotification("Please select an employee.", "warning");
      return;
    }
    if (!genMonth) {
      addNotification("Please select a month.", "warning");
      return;
    }

    setActionLoading(true);
    try {
      const payload = {
        employee: genEmployeeId,
        month: genMonth,
        basic_salary: genBasicSalary || undefined,
        bonus: genBonus || undefined,
        travel_allowance: genTravelAllowance || undefined,
        pf: genPf || undefined,
        tax: genTax || undefined,
        loss_of_pay_per_day: genLossOfPayPerDay || undefined,
      };
      const res = await employeeWorkforceAPI.previewPayroll(payload);
      const matchedEmp = employees.find(emp => String(emp.id) === String(genEmployeeId));
      let empName = `Employee ID: ${genEmployeeId}`;
      if (matchedEmp) {
        const roleObj = matchedEmp.roles?.[0];
        const roleLabel = roleObj?.name ? roleObj.name.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) : "";
        empName = `${matchedEmp.first_name} ${matchedEmp.last_name || ""}${roleLabel ? ` (${roleLabel})` : ""}`;
      }
      setPreviewData({
        ...res.data,
        employeeName: empName
      });
      setShowPreviewModal(true);
    } catch (err) {
      console.error(err);
      addNotification(err.response?.data?.detail || "Failed to calculate payroll preview.", "error");
    } finally {
      setActionLoading(false);
    }
  };

  const confirmGeneratePayroll = async () => {
    setActionLoading(true);
    try {
      const payload = {
        employee: genEmployeeId,
        month: genMonth,
        basic_salary: genBasicSalary || undefined,
        bonus: genBonus || undefined,
        travel_allowance: genTravelAllowance || undefined,
        pf: genPf || undefined,
        tax: genTax || undefined,
        loss_of_pay_per_day: genLossOfPayPerDay || undefined,
      };
      await employeeWorkforceAPI.generatePayroll(payload);
      addNotification("Payroll generated successfully!", "success");
      setShowPreviewModal(false);
      setPreviewData(null);
      
      // Clear manual input fields
      setGenBasicSalary("");
      setGenBonus("");
      setGenTravelAllowance("");
      setGenPf("");
      setGenTax("");
      setGenLossOfPayPerDay("");
      
      // Reload payrolls
      const payRes = await employeeWorkforceAPI.getPayrolls();
      setPayrolls(payRes.data);
      setFilterMonth(genMonth);
    } catch (err) {
      console.error(err);
      addNotification(err.response?.data?.detail || "Failed to generate payroll.", "error");
    } finally {
      setActionLoading(false);
    }
  };

  const executeMarkPaid = async (id) => {
    setActionLoading(true);
    try {
      await employeeWorkforceAPI.markPayrollPaid(id);
      addNotification("Payroll status marked as PAID.", "success");
      
      // Reload payrolls
      const payRes = await employeeWorkforceAPI.getPayrolls();
      setPayrolls(payRes.data);
    } catch (err) {
      console.error(err);
      addNotification("Failed to update payroll status.", "error");
    } finally {
      setActionLoading(false);
    }
  };

  // Mark Payroll PAID
  const handleMarkPaid = (id) => {
    setConfirmPaidId(id);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-slate-900">
        <EmptyState
          preset="payroll"
          title="Loading Payroll Data..."
          message="Please wait a moment while we calculate salary records."
        />
      </div>
    );
  }

  const statusBadges = {
    "PENDING": "bg-amber-50 text-amber-600 dark:bg-amber-950/30 dark:text-amber-400 border border-amber-200 dark:border-amber-800",
    "PAID": "bg-green-50 text-green-600 dark:bg-green-950/30 dark:text-green-400 border border-green-200 dark:border-green-800",
  };

  const filteredPayrolls = payrolls.filter(item => {
    return filterMonth ? item.month === filterMonth : true;
  });

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-slate-900 p-4 md:p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        
        {/* Header */}
        <div className="bg-white dark:bg-slate-800 p-6 md:p-8 rounded-3xl border border-gray-100 dark:border-slate-700 shadow-sm flex flex-col md:flex-row md:items-center justify-between gap-6">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-green-600 rounded-2xl text-white shadow-lg shadow-green-500/20">
              <CreditCard className="w-8 h-8" />
            </div>
            <div>
              <h1 className="text-2xl md:text-3xl font-black text-gray-900 dark:text-white">HRM Payroll & Salaries</h1>
              <p className="text-gray-500 dark:text-slate-400 font-medium">Configure basic structures, auto-calculate incentives/LOP, and issue salary disbursements</p>
            </div>
          </div>

          <button
            onClick={fetchData}
            className="flex items-center gap-2 px-5 py-2.5 bg-green-600 hover:bg-green-700 text-white rounded-2xl text-sm font-bold shadow-md shadow-green-500/10 active:scale-95 transition animate-pulse"
          >
            <RefreshCw className="w-4 h-4" />
            Refresh Ledger
          </button>
        </div>

        {/* Configuration and Generation panels */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          
          {/* Salary Structure Configuration */}
          <div className="bg-white dark:bg-slate-800 p-6 rounded-3xl border border-gray-100 dark:border-slate-700 shadow-sm space-y-4">
            <h2 className="text-lg font-black text-gray-900 dark:text-white uppercase tracking-wider mb-2 flex items-center gap-2">
              <Settings className="w-5 h-5 text-green-600" />
              Configure Salary Structure
            </h2>
            
            <form onSubmit={handleSaveConfig} className="space-y-4">
              <div>
                <label className="block text-xs font-bold text-gray-400 uppercase tracking-widest mb-2">Select Employee</label>
                <select
                  className="w-full px-4 py-3 bg-gray-50 dark:bg-slate-900 border border-gray-100 dark:border-slate-700 rounded-2xl focus:ring-2 focus:ring-green-500 transition text-sm dark:text-white appearance-none"
                  value={configEmployeeId}
                  onChange={(e) => handleConfigEmployeeChange(e.target.value)}
                  required
                >
                  <option value="">Choose Employee...</option>
                  {employees.map(emp => {
                    const roleObj = emp.roles?.[0];
                    const roleLabel = roleObj?.name ? roleObj.name.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) : "";
                    return (
                      <option key={emp.id} value={emp.id}>
                        {emp.first_name} {emp.last_name || ""}{roleLabel ? ` (${roleLabel})` : ""}
                      </option>
                    );
                  })}
                </select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-gray-400 uppercase tracking-widest mb-2">Basic Salary (₹)</label>
                  <input
                    type="number"
                    placeholder="e.g. 25000"
                    className="w-full px-4 py-3 bg-gray-50 dark:bg-slate-900 border border-gray-100 dark:border-slate-700 rounded-2xl focus:ring-2 focus:ring-green-500 transition text-sm dark:text-white"
                    value={basicSalary}
                    onChange={(e) => setBasicSalary(e.target.value)}
                    required
                  />
                </div>

                <div>
                  <label className="block text-xs font-bold text-gray-400 uppercase tracking-widest mb-2">Bonus (₹)</label>
                  <input
                    type="number"
                    placeholder="e.g. 2000"
                    className="w-full px-4 py-3 bg-gray-50 dark:bg-slate-900 border border-gray-100 dark:border-slate-700 rounded-2xl focus:ring-2 focus:ring-green-500 transition text-sm dark:text-white"
                    value={bonus}
                    onChange={(e) => setBonus(e.target.value)}
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-gray-400 uppercase tracking-widest mb-2">Travel Allowance (₹)</label>
                  <input
                    type="number"
                    placeholder="e.g. 1500"
                    className="w-full px-4 py-3 bg-gray-50 dark:bg-slate-900 border border-gray-100 dark:border-slate-700 rounded-2xl focus:ring-2 focus:ring-green-500 transition text-sm dark:text-white"
                    value={travelAllowance}
                    onChange={(e) => setTravelAllowance(e.target.value)}
                  />
                </div>

                <div>
                  <label className="block text-xs font-bold text-gray-400 uppercase tracking-widest mb-2">Loss of Pay Per Day (₹)</label>
                  <input
                    type="number"
                    placeholder="e.g. 500"
                    className="w-full px-4 py-3 bg-gray-50 dark:bg-slate-900 border border-gray-100 dark:border-slate-700 rounded-2xl focus:ring-2 focus:ring-green-500 transition text-sm dark:text-white"
                    value={lossOfPayPerDay}
                    onChange={(e) => setLossOfPayPerDay(e.target.value)}
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-gray-400 uppercase tracking-widest mb-2">PF Deduction (₹)</label>
                  <input
                    type="number"
                    placeholder="e.g. 1800"
                    className="w-full px-4 py-3 bg-gray-50 dark:bg-slate-900 border border-gray-100 dark:border-slate-700 rounded-2xl focus:ring-2 focus:ring-green-500 transition text-sm dark:text-white"
                    value={pf}
                    onChange={(e) => setPf(e.target.value)}
                  />
                </div>

                <div>
                  <label className="block text-xs font-bold text-gray-400 uppercase tracking-widest mb-2">Tax Deduction (₹)</label>
                  <input
                    type="number"
                    placeholder="e.g. 1200"
                    className="w-full px-4 py-3 bg-gray-50 dark:bg-slate-900 border border-gray-100 dark:border-slate-700 rounded-2xl focus:ring-2 focus:ring-green-500 transition text-sm dark:text-white"
                    value={tax}
                    onChange={(e) => setTax(e.target.value)}
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={actionLoading}
                className="w-full py-3.5 bg-green-600 hover:bg-green-700 text-white rounded-2xl font-bold flex items-center justify-center gap-2 transition active:scale-95 shadow-md shadow-green-500/10 text-sm"
              >
                {actionLoading ? <RefreshCw className="w-5 h-5 animate-spin" /> : "Save Salary Config"}
              </button>
            </form>
          </div>

          {/* Generate Payroll */}
          <div className="bg-white dark:bg-slate-800 p-6 rounded-3xl border border-gray-100 dark:border-slate-700 shadow-sm flex flex-col justify-between">
            <div className="space-y-4">
              <h2 className="text-lg font-black text-gray-900 dark:text-white uppercase tracking-wider flex items-center gap-2">
                <Calendar className="w-5 h-5 text-green-600" />
                Generate Month Payroll
              </h2>
              
              <p className="text-sm text-gray-500 dark:text-slate-400 font-medium">
                Generates a monthly pay ledger entry. The system will look up this employee's completed sales totals to calculate incentive slabs, and LOP deduction counts from marked ABSENT logs automatically.
              </p>

              <form onSubmit={handleGeneratePayroll} className="space-y-4 pt-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-bold text-gray-400 uppercase tracking-widest mb-2">Select Employee</label>
                    <select
                      className="w-full px-4 py-3 bg-gray-50 dark:bg-slate-900 border border-gray-100 dark:border-slate-700 rounded-2xl focus:ring-2 focus:ring-green-500 transition text-sm dark:text-white appearance-none"
                      value={genEmployeeId}
                      onChange={(e) => setGenEmployeeId(e.target.value)}
                      required
                    >
                      <option value="">Choose Employee...</option>
                      {employees.map(emp => {
                        const roleObj = emp.roles?.[0];
                        const roleLabel = roleObj?.name ? roleObj.name.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) : "";
                        return (
                          <option key={emp.id} value={emp.id}>
                            {emp.first_name} {emp.last_name || ""}{roleLabel ? ` (${roleLabel})` : ""}
                          </option>
                        );
                      })}
                    </select>
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-gray-400 uppercase tracking-widest mb-2">Month Period</label>
                    <input
                      type="month"
                      className="w-full px-4 py-3 bg-gray-50 dark:bg-slate-900 border border-gray-100 dark:border-slate-700 rounded-2xl focus:ring-2 focus:ring-green-500 transition text-sm dark:text-white font-bold"
                      value={genMonth}
                      onChange={(e) => setGenMonth(e.target.value)}
                      required
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-bold text-gray-400 uppercase tracking-widest mb-2">Basic Salary (₹)</label>
                    <input
                      type="number"
                      placeholder="e.g. 25000"
                      className="w-full px-4 py-3 bg-gray-50 dark:bg-slate-900 border border-gray-100 dark:border-slate-700 rounded-2xl focus:ring-2 focus:ring-green-500 transition text-sm dark:text-white"
                      value={genBasicSalary}
                      onChange={(e) => setGenBasicSalary(e.target.value)}
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-gray-400 uppercase tracking-widest mb-2">Bonus (₹)</label>
                    <input
                      type="number"
                      placeholder="e.g. 2000"
                      className="w-full px-4 py-3 bg-gray-50 dark:bg-slate-900 border border-gray-100 dark:border-slate-700 rounded-2xl focus:ring-2 focus:ring-green-500 transition text-sm dark:text-white"
                      value={genBonus}
                      onChange={(e) => setGenBonus(e.target.value)}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-bold text-gray-400 uppercase tracking-widest mb-2">Travel Allowance (₹)</label>
                    <input
                      type="number"
                      placeholder="e.g. 1500"
                      className="w-full px-4 py-3 bg-gray-50 dark:bg-slate-900 border border-gray-100 dark:border-slate-700 rounded-2xl focus:ring-2 focus:ring-green-500 transition text-sm dark:text-white"
                      value={genTravelAllowance}
                      onChange={(e) => setGenTravelAllowance(e.target.value)}
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-gray-400 uppercase tracking-widest mb-2">Loss of Pay Per Day (₹)</label>
                    <input
                      type="number"
                      placeholder="e.g. 500"
                      className="w-full px-4 py-3 bg-gray-50 dark:bg-slate-900 border border-gray-100 dark:border-slate-700 rounded-2xl focus:ring-2 focus:ring-green-500 transition text-sm dark:text-white"
                      value={genLossOfPayPerDay}
                      onChange={(e) => setGenLossOfPayPerDay(e.target.value)}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-bold text-gray-400 uppercase tracking-widest mb-2">PF Deduction (₹)</label>
                    <input
                      type="number"
                      placeholder="e.g. 1800"
                      className="w-full px-4 py-3 bg-gray-50 dark:bg-slate-900 border border-gray-100 dark:border-slate-700 rounded-2xl focus:ring-2 focus:ring-green-500 transition text-sm dark:text-white"
                      value={genPf}
                      onChange={(e) => setGenPf(e.target.value)}
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-gray-400 uppercase tracking-widest mb-2">Tax Deduction (₹)</label>
                    <input
                      type="number"
                      placeholder="e.g. 1200"
                      className="w-full px-4 py-3 bg-gray-50 dark:bg-slate-900 border border-gray-100 dark:border-slate-700 rounded-2xl focus:ring-2 focus:ring-green-500 transition text-sm dark:text-white"
                      value={genTax}
                      onChange={(e) => setGenTax(e.target.value)}
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={actionLoading}
                  className="w-full py-4 bg-green-600 hover:bg-green-700 text-white rounded-2xl font-bold flex items-center justify-center gap-2 transition active:scale-95 shadow-md shadow-green-500/20 text-md"
                >
                  {actionLoading ? <RefreshCw className="w-5 h-5 animate-spin" /> : "Calculate & Generate Slip"}
                </button>
              </form>
            </div>
            <div className="mt-8 border-t border-gray-100 dark:border-slate-700 pt-6">
              <p className="text-xs font-black text-gray-400 uppercase tracking-widest">Calculated Components:</p>
              <ul className="text-xs text-gray-500 dark:text-slate-400 mt-2 space-y-1 font-medium list-disc pl-4">
                <li>Sales Incentive: calculated automatically from POS invoices</li>
                <li>Loss of Pay (LOP): calculated from ABSENT attendance records</li>
                <li>Net Salary = Basic + Incentive + Allowances - Deductions - LOP</li>
              </ul>
            </div>
          </div>

        </div>

        {/* Ledger view with Month filter */}
        <div className="bg-white dark:bg-slate-800 rounded-3xl border border-gray-100 dark:border-slate-700 shadow-sm overflow-hidden">
          <div className="p-6 border-b border-gray-50 dark:border-slate-700 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <h2 className="text-lg font-black text-gray-900 dark:text-white uppercase tracking-wider">
              Disbursement Slips & Ledger
            </h2>
            
            <div className="flex items-center gap-3">
              <span className="text-xs font-bold text-gray-400 uppercase tracking-widest">Ledger Period:</span>
              <input
                type="month"
                className="px-4 py-2 bg-gray-50 dark:bg-slate-900 border border-gray-100 dark:border-slate-800 rounded-xl text-xs font-bold focus:ring-1 focus:ring-green-500 transition dark:text-white"
                value={filterMonth}
                onChange={(e) => setFilterMonth(e.target.value)}
              />
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead className="bg-gray-50 dark:bg-slate-900/50 border-b border-gray-100 dark:border-slate-700">
                <tr>
                  <th className="px-6 py-4 text-xs font-bold uppercase text-gray-400 tracking-wider">Employee</th>
                  <th className="px-6 py-4 text-xs font-bold uppercase text-gray-400 tracking-wider">Period</th>
                  <th className="px-6 py-4 text-xs font-bold uppercase text-gray-400 tracking-wider">Basic Salary</th>
                  <th className="px-6 py-4 text-xs font-bold uppercase text-gray-400 tracking-wider">Incentive Commission</th>
                  <th className="px-6 py-4 text-xs font-bold uppercase text-gray-400 tracking-wider">Deductions (LOP/PF/Tax)</th>
                  <th className="px-6 py-4 text-xs font-bold uppercase text-gray-400 tracking-wider">Net Payable</th>
                  <th className="px-6 py-4 text-xs font-bold uppercase text-gray-400 tracking-wider">Status</th>
                  <th className="px-6 py-4 text-xs font-bold uppercase text-gray-400 tracking-wider text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-slate-700">
                {filteredPayrolls.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="px-6 py-12 text-center text-gray-400 font-bold">
                      No payroll records generated for the selected month.
                    </td>
                  </tr>
                ) : filteredPayrolls.map(item => {
                  const emp = employees.find(e => String(e.id) === String(item.employee)) || item.employee_details;
                  const deductions = parseFloat(item.pf) + parseFloat(item.tax) + parseFloat(item.loss_of_pay);
                  
                  return (
                    <tr key={item.id} className="hover:bg-gray-50/50 dark:hover:bg-slate-800/30 transition-colors">
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-lg bg-green-500/10 text-green-600 flex items-center justify-center font-black text-xs">
                            {emp?.first_name?.[0] || "E"}{emp?.last_name?.[0] || ""}
                          </div>
                          <div>
                            <p className="font-bold text-gray-900 dark:text-white">
                              {emp ? `${emp.first_name} ${emp.last_name || ""}` : `Employee ID: ${item.employee}`}
                            </p>
                            <p className="text-[9px] text-gray-400 font-semibold tracking-wider uppercase mt-0.5">
                              {emp?.roles?.[0]?.name 
                                ? emp.roles[0].name.replace(/_/g, ' ') 
                                : (emp?.role || "SALES EXECUTIVE")}
                            </p>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 font-bold text-gray-900 dark:text-white">
                        {item.month}
                      </td>
                      <td className="px-6 py-4 text-gray-600 dark:text-slate-400">
                        ₹{parseFloat(item.basic_salary).toLocaleString()}
                      </td>
                      <td className="px-6 py-4 text-green-600 dark:text-green-400 font-medium">
                        +₹{parseFloat(item.sales_incentive).toLocaleString()}
                      </td>
                      <td className="px-6 py-4 text-red-500 font-medium">
                        -₹{deductions.toLocaleString()}
                        {parseFloat(item.loss_of_pay) > 0 && (
                          <span className="text-[10px] block font-bold text-amber-500 uppercase">
                            LOP Included
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 font-black text-gray-950 dark:text-white">
                        ₹{parseFloat(item.net_salary).toLocaleString()}
                      </td>
                      <td className="px-6 py-4">
                        <span className={`px-2.5 py-0.5 rounded-full text-[10px] font-black uppercase tracking-widest ${statusBadges[item.status]}`}>
                          {item.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-right">
                        {item.status === "PENDING" ? (
                          <button
                            onClick={() => handleMarkPaid(item.id)}
                            disabled={actionLoading}
                            className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-xl text-xs font-bold transition shadow-xs hover:shadow active:scale-95"
                          >
                            Mark Paid
                          </button>
                        ) : (
                          <span className="text-xs font-black text-green-600 dark:text-green-400 flex items-center justify-end gap-1">
                            <CheckCircle className="w-4 h-4" />
                            Disbursed
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* Review & Approve Payroll Modal */}
        {showPreviewModal && previewData && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-xs">
            <div className="bg-white dark:bg-slate-800 rounded-3xl border border-gray-100 dark:border-slate-700 shadow-2xl w-full max-w-lg overflow-hidden transform transition-all animate-in fade-in duration-200">
              {/* Header */}
              <div className="p-6 border-b border-gray-50 dark:border-slate-700 flex justify-between items-center">
                <div>
                  <h3 className="text-lg font-black text-gray-900 dark:text-white uppercase tracking-wider">
                    Review & Approve Payroll
                  </h3>
                  <p className="text-xs text-gray-500 dark:text-slate-400 font-medium mt-1">
                    For {previewData.employeeName} - {previewData.month}
                  </p>
                </div>
                <button 
                  onClick={() => { setShowPreviewModal(false); setPreviewData(null); }}
                  className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-full transition"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Content */}
              <div className="p-6 space-y-4">
                <div className="bg-slate-50 dark:bg-slate-900/50 rounded-2xl p-5 border border-slate-100 dark:border-slate-800 space-y-3">
                  <div className="flex justify-between text-sm font-medium text-gray-600 dark:text-slate-400">
                    <span>Basic Salary</span>
                    <span className="font-bold text-gray-900 dark:text-white">₹{previewData.basic_salary.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between text-sm font-medium text-green-600 dark:text-green-400">
                    <span>Sales Incentive</span>
                    <span className="font-bold">+₹{previewData.sales_incentive.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between text-sm font-medium text-green-600 dark:text-green-400">
                    <span>Bonus</span>
                    <span className="font-bold">+₹{previewData.bonus.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between text-sm font-medium text-green-600 dark:text-green-400">
                    <span>Travel Allowance</span>
                    <span className="font-bold">+₹{previewData.travel_allowance.toLocaleString()}</span>
                  </div>
                  
                  <div className="border-t border-slate-200/50 dark:border-slate-800 pt-3 flex justify-between text-sm font-medium text-red-500">
                    <span>PF Deduction</span>
                    <span className="font-bold">-₹{previewData.pf.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between text-sm font-medium text-red-500">
                    <span>Income Tax</span>
                    <span className="font-bold">-₹{previewData.tax.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between text-sm font-medium text-red-500">
                    <span>Loss of Pay (LOP)</span>
                    <span className="font-bold">-₹{previewData.loss_of_pay.toLocaleString()}</span>
                  </div>

                  <div className="border-t border-slate-200 dark:border-slate-700 pt-4 flex justify-between items-center">
                    <span className="text-md font-black text-gray-900 dark:text-white">Net Take-Home</span>
                    <span className="text-xl font-black text-green-600 dark:text-green-400 flex items-center">
                      <IndianRupee className="w-5 h-5 mr-1" />
                      {previewData.net_salary.toLocaleString()}
                    </span>
                  </div>
                </div>

                <div className="text-xs text-gray-400 dark:text-slate-500 font-medium">
                  * Confirming this will generate the monthly payslip and log the payroll record under the pending ledgers.
                </div>
              </div>

              {/* Actions */}
              <div className="bg-gray-50 dark:bg-slate-900/30 p-6 border-t border-gray-50 dark:border-slate-700 flex gap-4">
                <button
                  type="button"
                  onClick={() => { setShowPreviewModal(false); setPreviewData(null); }}
                  className="flex-1 py-3 bg-gray-100 hover:bg-gray-200 dark:bg-slate-700 dark:hover:bg-slate-600 text-gray-700 dark:text-slate-200 rounded-2xl text-sm font-bold transition active:scale-95"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={confirmGeneratePayroll}
                  disabled={actionLoading}
                  className="flex-1 py-3 bg-green-600 hover:bg-green-700 text-white rounded-2xl text-sm font-bold transition active:scale-95 flex items-center justify-center gap-2 shadow-md shadow-green-500/20"
                >
                  {actionLoading ? <RefreshCw className="w-4 h-4 animate-spin" /> : "Approve & Generate"}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Custom Mark Paid Confirmation Modal */}
        {confirmPaidId && (
            <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[60] flex items-center justify-center p-4">
                <motion.div 
                    initial={{ scale: 0.9, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    className="bg-white dark:bg-dark-card rounded-2xl shadow-2xl max-w-sm w-full overflow-hidden border border-gray-100 dark:border-dark-border"
                >
                    <div className="p-6 text-center">
                        <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4">Confirm Action</h3>
                        <p className="text-sm text-gray-600 dark:text-gray-400 mb-6 font-medium">
                            Are you sure you want to mark this payroll as PAID?
                        </p>
                        <div className="grid grid-cols-2 gap-3">
                            <button
                                onClick={() => setConfirmPaidId(null)}
                                className="px-4 py-2.5 bg-gray-100 hover:bg-gray-200 dark:bg-dark-border dark:hover:bg-gray-700 text-gray-700 dark:text-gray-200 font-bold rounded-xl transition-all cursor-pointer text-sm"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={() => {
                                    executeMarkPaid(confirmPaidId);
                                    setConfirmPaidId(null);
                                }}
                                className="px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-xl transition-all shadow-md shadow-blue-500/10 cursor-pointer text-sm"
                            >
                                OK
                            </button>
                        </div>
                    </div>
                </motion.div>
            </div>
        )}
      </div>
    </div>
  );
}
